<template>
    <div
        class="full-calendar"
        data-test="full-calendar"
    >
        <Calendar
            class="max-w-full"
            :masks="Masks"
            :attributes="items"
            :initial-page="initialPage"
            :locale="vCalendarLocale"
            disable-page-swipe
            is-expanded
            @did-move="$emit('month-change', $event)"
        >
            <template #day-content="{ day: data, attributes }">
                <div class="calendar-day-content flex flex-col p-0 h-full z-10 overflow-hidden">
                    <div
                        :class="['calendar-day-content__day font-bold', { 'calendar-day-content__day--today': data.isToday }]"
                    >
                        {{ data.day }}
                    </div>
                    <div class="calendar-day-content__items flex flex-col">
                        <span
                            v-for="attr in attributes.slice(0, MAX_VISIBLE_DAY_ITEMS)"
                            :key="attr.customData.id"
                            data-test="full-calendar-item"
                            class="calendar-day-content__items__item text-xs rounded-md mt-0 mb-0 font-bold px-1  py-0 cursor-pointer line-clamp-1"
                            @click="$emit('item-click', attr.customData.id)"
                            v-html="eventTitle(attr, data.day)"
                        />
                        <span
                            v-if="attributes.length > MAX_VISIBLE_DAY_ITEMS"
                            data-test="full-calendar-items-more"
                            class="calendar-day-content__items__more cursor-pointer font-bold overflow-hidden leading-[13px]"
                            @click="$emit('item-show-more-click', attributes, moment(data.date).toISOString())"
                            v-html="showMoreTitle(attributes.length)"
                        />
                    </div>
                </div>
            </template>
        </Calendar>
    </div>
</template>

<script setup lang="ts">
    import { Calendar } from 'v-calendar';
    import { computed } from 'vue';
    import type { FullCalendarItem } from '~/types/fullCalendar';
    import moment from 'moment';
    import { useI18n } from 'vue-i18n';
    import { eventTitle } from '~/utils/eventCalendar';
    import { useWindowSize } from '@vueuse/core';

    const MAX_VISIBLE_DAY_ITEMS = 2;

    type FullCalendarProps = {
        items: FullCalendarItem[];
        locale: string;
        primaryColor: string;
        secondaryColor: string;
        initialYear?: number;
        initialMonth?: number;
    };

    const Masks = {
        weekdays: 'WWW',
    };

    const { t } = useI18n();
    defineEmits(['item-click', 'month-change', 'item-show-more-click']);
    const props = defineProps<FullCalendarProps>();

    const { width } = useWindowSize();
    const vCalendarLocale = computed(() => props.locale.split('_')[0]);
    const isMinimalLayout = computed(() => width.value < 650);

    const showMoreTitle = (count: number) => {
        const extraCount = count - MAX_VISIBLE_DAY_ITEMS;
        if (isMinimalLayout.value) {
            return `+ ${extraCount}`;
        }
        return `${extraCount}&nbsp;${t('eventCalendar.events.more', extraCount)}`;
    };

    const initialPage = {
        year: props.initialYear ?? moment().year(),
        month: props.initialMonth ?? moment().month() + 1
    };
</script>

<style lang="scss" scoped>
$body-bg: #f7f7f7;
$day-border: 1px solid #b8c2cc;
$day-border-highlight: 1px solid #b8c2cc;

.full-calendar {
  :deep(.vc-container) {
    border-radius: 0;
    width: 100%;
    border: unset;
    .vc-weeks {
      padding: 0;
      border-left: $day-border-highlight;
      border-right: $day-border-highlight;
    }
    .vc-week {
      display: flex;
    }
    .vc-title-wrapper {
      z-index: 10;
      span {
        font-weight: bold;
        font-size: 18px;
      }
    }
    .vc-header {
      background-color: v-bind(primaryColor);
      margin: 0;
      padding: 10px 0;
      height: 50px;
    }
    .vc-title, .vc-arrow {
      background-color: v-bind(primaryColor);
      color: v-bind(secondaryColor);
      opacity: 1;
      border: none;
      outline: none;
      box-shadow: none;
    }
    .vc-weekday {
      border-bottom: $day-border-highlight;
      color: v-bind(primaryColor);
      padding: 5px 0;
    }
    .vc-day {
      height: 100px;
      padding-right: 8px;
      background-color: $body-bg;
      border-bottom: $day-border;
      box-sizing: border-box;
      width: calc(100% / 7);
      &.weekday-1 {
        border-bottom: $day-border-highlight;
      }
      &:not(.on-right) {
        border-right: $day-border;
      }
    }
    .vc-day-dots {
      margin-bottom: 5px;
    }
    .calendar-day-content {
      display: flex;
      flex-direction: column;
      flex-wrap: nowrap;
      &__day {
        font-size: 12px;
        min-height: 24px;
        min-width: 24px;
        display: inline-flex;
        justify-content: center;
        align-items: center;
        margin: 2px auto 5px auto;
        &--today {
          border-radius: 50%;
          color: v-bind(secondaryColor);
          background-color: v-bind(primaryColor);
        }
      }
      &__items {
        gap: 0.15rem;
        &__item {
          font-size: 12px;
          background-color: v-bind(primaryColor);
          color: v-bind(secondaryColor);
        }
        &__more {
          font-size: 12px;
          margin-top: 2px;
          margin-left: 0.25rem;
        }
      }
    }
  }
  :deep(.vc-popover-content-wrapper) {
    .vc-nav-item.is-active {
      background-color: v-bind(primaryColor);
    }
  }

    @media (max-width: theme('screens.sm')) {
    :deep(.vc-container) {
      .vc-day {
        height: 80px;
        padding-left: 4px;
        padding-right: 4px;
      }
    }
    .calendar-day-content {
      &__items {
        &__item {
          width: 80%;
          height: 10px;
          max-height: 10px;
          overflow: hidden;
          color: transparent !important;
        }
      }
    }
  }
}
</style>
